// -----------------------------------------------------------------------
// PNG/JPEG export override for jsv2.SpectraViewer1D
// -----------------------------------------------------------------------
// jsv2's SpectraViewer1D.image() hard-codes the y range to [height, 0]
// (non-reversed) both while drawing the exported image and when restoring
// the on-screen view afterwards. For IR (axis y reverse: true) that exports
// the spectrum upside down AND leaves the live graph flipped after a
// download. This is the same routine, but it uses the viewer's current y
// direction: reversed for a single IR spectrum, normal in mirror mode
// (jsv2 inverts the IR data itself when a mirror spectrum is added).

import * as jsv2 from 'jsv2';

jsv2.SpectraViewer1D.prototype.image = function(width, height) {
    width = width || this.width;
    height = height || this.height;

    // jsv2's internal pixel ratio: scaled_height = (height - axis_x_gutter) * ratio
    const ratio = this.scaled_height / (this.height - this.axis_x_gutter) || 1;
    const yReversed = this.axis_y_reverse && !this.has_mirror_plot;
    const yRange = (scaledHeight) => yReversed ? [0, scaledHeight] : [scaledHeight, 0];

    const origContext = this.context;
    const zoomboxVisible = this.zoombox.visible;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = width * ratio;
    tempCanvas.height = height * ratio;
    tempCanvas.style.width = width + "px";
    tempCanvas.style.height = height + "px";
    document.body.appendChild(tempCanvas);

    try {
        this.context = tempCanvas.getContext("2d");
        this.scale.x.range(this.x_range(width));
        this.scale.y.range(yRange((height - this.axis_x_gutter) * ratio));
        this.zoombox.visible = false;

        this.context.fillStyle = "white";
        this.context.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        this.full_draw();
        return tempCanvas.toDataURL();
    } finally {
        // Restore the live view exactly as it was
        this.scale.x.range(this.x_range());
        this.scale.y.range(yRange(this.scaled_height));
        this.context = origContext;
        this.zoombox.visible = zoomboxVisible;
        tempCanvas.remove();
        this.full_draw();
    }
};

// -----------------------------------------------------------------------
// SVG export override
// -----------------------------------------------------------------------
// jsv2's SVMenu.generateSVG always draws y increasing upward and x
// increasing left to right, ignoring the viewer's axis reversal, so IR (and
// NMR's reversed x) export mirrored. It positions everything (lines, ticks,
// labels) from sv.scale.x/y.domain() alone, so passing it a view of the
// viewer whose domains are ordered as on screen ([max, min] for a reversed
// axis) makes the SVG match the canvas.
//
// SVMenu isn't exported by jsv2, so it's reached through the first viewer's
// menu instance (viewer.menu.constructor) and patched once.

function screenOrder(domain, reversed) {
    const [lo, hi] = [Math.min(...domain), Math.max(...domain)];
    return reversed ? [hi, lo] : [lo, hi];
}

function patchSvgExport(SVMenu) {
    if (SVMenu.__svgExportPatched) return;
    const originalGenerateSVG = SVMenu.generateSVG;

    SVMenu.generateSVG = function(sv, width, height) {
        const xDomain = screenOrder(sv.scale.x.domain(), sv.axis_x_reverse);
        const yDomain = screenOrder(sv.scale.y.domain(), sv.axis_y_reverse && !sv.has_mirror_plot);
        const view = Object.create(sv);  // everything else is read through to the real viewer
        view.scale = { x: { domain: () => xDomain }, y: { domain: () => yDomain } };
        return originalGenerateSVG.call(this, view, width, height);
    };
    SVMenu.__svgExportPatched = true;
}

for (const Viewer of [jsv2.SpectraViewer1D, jsv2.SpectraViewer2D]) {
    const originalDraw = Viewer.prototype.draw;
    Viewer.prototype.draw = function(...args) {
        if (this.menu) patchSvgExport(this.menu.constructor);
        return originalDraw.apply(this, args);
    };
}
